import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { SYSIGREJA_INTERNO_LOGIN_ID } from "@/constants/sysigreja-interno-login-id";

const eventosTrialBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { baseQueryWithReauth } = await import("@/lib/api");
  const fetchArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };
  const relativeUrl =
    typeof fetchArgs.url === "string" ? fetchArgs.url.replace(/^\//, "") : fetchArgs.url;
  const url = `/eventos-trial/${relativeUrl}`;
  return baseQueryWithReauth({ ...fetchArgs, url }, api, extraOptions);
};

export type EventoTrialCheckEmailStatus = "new" | "same_org" | "other_org";

export type ResolveMapsLocalResult = {
  nomeLocal: string | null;
  finalUrl: string | null;
  source: "url" | "redirect" | "og" | null;
};

export type MeuEventoTrialResult = {
  eventoId: number;
  configuracaoId: number | null;
  identificador: string | null;
  titulo: string;
  dataEvento: string | null;
  nomeLocal: string | null;
  valor: number;
  publicado: boolean;
  loginIdentificador: string | null;
  loginSlug: string | null;
  capaUrl: string | null;
};

/** Progresso do "Guia do evento" (contagens do servidor + itens marcados na tela). */
export type GuiaTrialResult = {
  eventoId: number;
  configuracaoId: number | null;
  publicado: boolean;
  dataEvento: string | null;
  dataFim: string | null;
  encerrado: boolean;
  inscritos: number;
  mensagens: number;
  cupons: number;
  crachas: number;
  marcos: Partial<Record<"tour" | "link" | "pagina" | "checkin", string>>;
};

export type FunilTrialResult = {
  dias: number;
  lancados: number;
  etapas: Array<{ chave: string; label: string; total: number }>;
  medianaHorasPrimeiraInscricao: number | null;
  semInscricaoApos7Dias: number;
};

export const eventosTrialAPI = createApi({
  reducerPath: "eventosTrialAPI",
  baseQuery: eventosTrialBaseQuery,
  endpoints: (build) => ({
    checkEmailTrial: build.mutation<{ status: EventoTrialCheckEmailStatus }, { email: string }>({
      query: (body) => ({ url: `check-email`, method: "POST", body }),
    }),
    registrarWaitlistTrial: build.mutation<
      { status: "registered" | "already_registered" },
      { email: string }
    >({
      query: (body) => ({ url: `waitlist`, method: "POST", body }),
    }),
    resolveMapsLocal: build.query<ResolveMapsLocalResult, { url: string }>({
      query: ({ url }) => ({
        url: `resolve-maps-local?url=${encodeURIComponent(url)}`,
        method: "GET",
      }),
    }),
    getMeuEventoTrial: build.query<MeuEventoTrialResult | null, void>({
      query: () => ({
        url: `meu-evento`,
        method: "GET",
        params: { loginId: SYSIGREJA_INTERNO_LOGIN_ID },
      }),
    }),
    getFunilTrial: build.query<FunilTrialResult, { dias: number }>({
      query: ({ dias }) => ({
        url: `admin/funil`,
        method: "GET",
        params: { dias, loginId: SYSIGREJA_INTERNO_LOGIN_ID },
      }),
    }),
    getGuiaTrial: build.query<GuiaTrialResult | null, void>({
      query: () => ({
        url: `meu-evento/guia`,
        method: "GET",
        params: { loginId: SYSIGREJA_INTERNO_LOGIN_ID },
      }),
    }),
  }),
});

export const {
  useCheckEmailTrialMutation,
  useRegistrarWaitlistTrialMutation,
  useLazyResolveMapsLocalQuery,
  useLazyGetMeuEventoTrialQuery,
  useGetGuiaTrialQuery,
  useGetFunilTrialQuery,
} = eventosTrialAPI;
